// Cookie / analytics consent store. Choices are persisted in localStorage so
// the banner only appears once. Firebase Analytics is gated on the analytics
// category being on AND on the COPPA/RGPD-K age gate (see
// `utils/trackingPolicy.js`). Cookie consent is necessary but not sufficient:
// a kid-mode user who toggles analytics on still gets the SDK kill switch.
//
// v2 storage shape:
//   { decided: true, categories: { necessary, analytics, marketing } }
// Legacy v1 strings ("accepted" / "rejected") are migrated transparently.

import { reactive } from "vue";
import { currentUserAge, canTrack } from "../utils/trackingPolicy";

const STORAGE_KEY = "cogito_cookie_consent_v2";
const LEGACY_KEY = "cogito_cookie_consent";

function defaultCategories() {
  return { necessary: true, analytics: false, marketing: false };
}

function readStored() {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.categories) return parsed;
    }
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy === "accepted") {
      return { decided: true, categories: { necessary: true, analytics: true, marketing: true } };
    }
    if (legacy === "rejected") {
      return { decided: true, categories: defaultCategories() };
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

function writeStored(state) {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    // Keep the legacy key in sync as a coarse boolean for any old reader.
    const anyOptIn = state.categories.analytics || state.categories.marketing;
    localStorage.setItem(LEGACY_KEY, anyOptIn ? "accepted" : "rejected");
  } catch (_) {
    /* ignore quota / privacy mode failures */
  }
}

const stored = readStored();

export const consentState = reactive({
  decided: !!stored,
  categories: stored ? { ...defaultCategories(), ...stored.categories } : defaultCategories(),
  accepted: stored ? (stored.categories.analytics || stored.categories.marketing) : false,
  settingsOpen: false,
});

function finalize() {
  consentState.decided = true;
  consentState.accepted = consentState.categories.analytics || consentState.categories.marketing;
  consentState.settingsOpen = false;
  writeStored({ decided: true, categories: { ...consentState.categories } });
  applyAnalyticsConsent();
}

function applyAnalyticsConsent() {
  if (!consentState.categories.analytics) {
    // Analytics not opted-in — do not initialise the SDK. Existing instance
    // (if any) keeps its kill switch on via setTrackingEligible(false).
    return;
  }
  // Kid mode (under 16) overrides the toggle: never enable analytics.
  const age = currentUserAge();
  if (age != null && !canTrack(age)) return;
  import("../firebase").then(async (m) => {
    if (m.enableAnalytics) {
      await m.enableAnalytics();
    }
    if (m.setAnalyticsUser) {
      const { auth } = await import("../firebase");
      const uid = auth.currentUser ? auth.currentUser.uid : null;
      if (uid) m.setAnalyticsUser(uid);
    }
  });
}

export function acceptAllCookies() {
  consentState.categories = { necessary: true, analytics: true, marketing: true };
  finalize();
}

export function rejectAllCookies() {
  consentState.categories = defaultCategories();
  finalize();
}

export function saveCookieSelection({ analytics, marketing }) {
  consentState.categories = {
    necessary: true,
    analytics: !!analytics,
    marketing: !!marketing,
  };
  finalize();
}

export function openCookieSettings() {
  consentState.settingsOpen = true;
}

export function closeCookieSettings() {
  consentState.settingsOpen = false;
}

export function consentGivenAtBoot() {
  return !!stored && (stored.categories.analytics || stored.categories.marketing);
}

// Backward-compat helpers — kept so any external caller keeps working.
export function acceptCookies() {
  acceptAllCookies();
}

export function rejectCookies() {
  rejectAllCookies();
}
